import React from 'react';
import { useParams } from 'react-router-dom';
import { PageLayout } from '../components/global/PageLayout';
import { PageIntro, Section } from '../components/ui/PageIntro';
import { LegalNav } from '../components/legal/LegalNav';
import { LegalDocBody, clauseId } from '../components/legal/LegalDocBody';
import { getLegalDoc } from '../data/legal';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { NotFound } from './NotFound';
export function LegalDocument() {
  const {
    slug
  } = useParams();
  const doc = slug ? getLegalDoc(slug) : undefined;
  if (!doc) return <NotFound data-id="element-680" data-mp="2dicmo0o9iik1" />;
  return <LegalDocumentView key={doc.slug} slug={doc.slug} data-id="element-681" data-mp="2divtomcdjmja" />;
}

/**
 * One legal document, at /legal/<slug>.
 *
 * Three ways through a long document, so nobody has to scroll hunting for
 * a clause: the document navigation, which is on every legal page and
 * marks where you are; "On this page", which lists the clauses of this
 * document; and each clause heading as its own anchor, so a specific
 * clause can be cited rather than "see our privacy policy".
 *
 * Split out so the metadata hook is never called conditionally, and so
 * moving between two documents remounts rather than keeping the previous
 * one's scroll position against the new content.
 */
function LegalDocumentView({
  slug
}: {
  slug: string;
}) {
  const doc = getLegalDoc(slug)!;
  useDocumentMeta({
    title: `${doc.title} | Low On Fuel`,
    description: doc.description,
    path: `/legal/${doc.slug}`
  });
  const published = doc.sections.filter(section => section.body !== null);
  return <PageLayout data-id="element-682" data-mp="16qhrk5yc0sup">
      <PageIntro eyebrow="Legal" title={doc.title} intro={doc.summary} crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Legal',
      to: '/legal/privacy'
    }, {
      label: doc.title
    }]} data-id="element-683" data-mp="1bjsuvx3xqrhv" />

      <Section labelledBy="legal-document-heading" data-id="element-684" data-mp="1km266p712owb">
        <h2 id="legal-document-heading" className="sr-only" data-id="element-685" data-mp="1pisjj4rg76e1">
          {doc.title}
        </h2>

        {/* Source order puts the document first, so a screen reader and a
            small screen both reach the content before the two navigations.
            The grid moves the aside alongside on wide viewports only. */}
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-686" data-mp="3vd1s9qrphjm2">
          <div className="lg:col-span-8" data-id="element-687" data-mp="3qltekijcfc33">
            {doc.lastUpdated && <p className="text-sm font-medium text-ink-muted" data-id="element-688" data-mp="3qjqknnf2kyt3">
                Last updated {doc.lastUpdated}
              </p>}
            <div className="mt-8" data-id="element-689" data-mp="44xcrt5ucuc6">
              <LegalDocBody doc={doc} data-id="element-690" data-mp="45p58v456by8" />
            </div>
          </div>

          <aside className="lg:col-span-4 lg:col-start-9 lg:row-start-1" data-id="element-691" data-mp="i4gkwlynk5oh">
            <div className="flex flex-col gap-6" data-id="element-692" data-mp="d6576058lb0z">
              {published.length > 1 && <nav aria-label={`Clauses of the ${doc.title.toLowerCase()}`} className="rounded-container border border-hairline bg-white p-4" data-id="element-693" data-mp="2jwl8xmd16itx">
                  <h3 className="px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-ink-muted" data-id="element-694" data-mp="ufhbhz4fcxr0">
                    On this page
                  </h3>
                  <ol className="mt-2 flex flex-col" data-id="element-695" data-mp="zh6kl98371cg">
                    {published.map((section, index) => <li key={section.heading} data-id="element-696" data-mp="pp9bc80n2jgd" data-mpi={index}>
                        <a href={`#${clauseId(section.heading)}`} className="flex min-h-[44px] items-center gap-3 px-3 py-2 text-sm font-medium text-ink-muted transition-colors duration-200 ease-out-expo hover:text-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-697" data-mp="pny6s5zdd7o7">
                          <span aria-hidden="true" className="shrink-0 text-xs font-bold text-ink-muted" data-id="element-698" data-mp="6pvdhezsgokl">
                            {index + 1}
                          </span>
                          {section.heading}
                        </a>
                      </li>)}
                  </ol>
                </nav>}

              <LegalNav data-id="element-699" data-mp="17f8afnu2thc3" />
            </div>
          </aside>
        </div>
      </Section>
    </PageLayout>;
}